import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Input } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"

import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"

import {
  useCreateAccountBankAccount,
  useEditAccountBankAccount,
} from "hooks/useAccountBankAccount"
import { useRtgsBankTemplates } from "hooks/useRtgsBankTemplate"

const AccountBankAccountAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountBankAccount()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccountBankAccount()

  const { data: bankTemplateResponse } = useRtgsBankTemplates({
    all: true,
    is_active: true,
  })
  const bankTemplates =
    bankTemplateResponse?.data?.data || bankTemplateResponse?.data || []

  const validationSchema = Yup.object({
    label: Yup.string().nullable(),
    account_name: Yup.string().trim().required("Please enter account name"),
    account_number: Yup.string().trim().required("Please enter account number"),
    routing_number: Yup.string().nullable(),
    account_mobile_number: Yup.string()
      .trim()
      .required("Please enter account mobile number")
      .max(20, "Account mobile number cannot exceed 20 characters"),
    bank_name: Yup.string().trim().required("Please enter bank name"),
    rtgs_bank_template_id: Yup.number().nullable(),
    branch_name: Yup.string().nullable(),
    is_default: Yup.boolean(),
    is_active: Yup.boolean().required(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        label: values.label?.trim() || null,
        account_name: values.account_name?.trim(),
        account_number: values.account_number?.trim(),
        routing_number: values.routing_number?.trim() || null,
        account_mobile_number: values.account_mobile_number?.trim() || null,
        bank_name: values.bank_name?.trim(),
        rtgs_bank_template_id: values.rtgs_bank_template_id || null,
        branch_name: values.branch_name?.trim() || null,
        is_default: !!values.is_default,
        is_active: !!values.is_active,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Bank account ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        toast.error("Failed to save bank account.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      label: editedData?.label || "",
      account_name: editedData?.account_name || "",
      account_number: editedData?.account_number || "",
      routing_number: editedData?.routing_number || "",
      account_mobile_number: editedData?.account_mobile_number || "",
      bank_name: editedData?.bank_name || "",
      rtgs_bank_template_id: editedData?.rtgs_bank_template_id || "",
      branch_name: editedData?.branch_name || "",
      is_default: editedData?.is_default ?? false,
      is_active: editedData?.is_active ?? true,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Bank Account"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading,
    [editLoading, createLoading]
  )

  const renderField = (key, label, opts = {}) => (
    <div className="mb-3">
      <Label htmlFor={key}>{label}</Label>
      <InputField
        type="text"
        name={key}
        id={key}
        value={formik.values[key]}
        onChange={formik.handleChange}
        invalid={formik.touched[key] && !!formik.errors[key]}
        {...opts}
      />
      {formik.touched[key] && formik.errors[key] && (
        <FormFeedback type="invalid">{formik.errors[key]}</FormFeedback>
      )}
    </div>
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Bank Account" : "Add Bank Account"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        {renderField("label", "Label (optional)")}
        {renderField("account_name", "Account Name")}
        {renderField("account_number", "Account Number")}
        {renderField("routing_number", "Routing Number (optional)")}
        {renderField("account_mobile_number", "Account Mobile Number")}
        {renderField("bank_name", "Bank Name")}

        <div className="mb-3">
          <Label htmlFor="rtgs_bank_template_id">RTGS Pad Template (optional)</Label>
          <Input
            type="select"
            name="rtgs_bank_template_id"
            id="rtgs_bank_template_id"
            value={formik.values.rtgs_bank_template_id}
            onChange={formik.handleChange}
            invalid={
              formik.touched.rtgs_bank_template_id &&
              !!formik.errors.rtgs_bank_template_id
            }
          >
            <option value="">— None (use default pad) —</option>
            {bankTemplates.map(t => (
              <option key={t.id} value={t.id}>
                {t.bank_name}
              </option>
            ))}
          </Input>
          {formik.touched.rtgs_bank_template_id &&
            formik.errors.rtgs_bank_template_id && (
              <FormFeedback type="invalid">
                {formik.errors.rtgs_bank_template_id}
              </FormFeedback>
            )}
        </div>

        {renderField("branch_name", "Branch Name (optional)")}

        <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
          <Button
            type="button"
            color={formik.values.is_default ? "success" : "light"}
            outline={!formik.values.is_default}
            size="sm"
            onClick={() =>
              formik.setFieldValue("is_default", !formik.values.is_default)
            }
          >
            <i
              className={`bx ${
                formik.values.is_default ? "bx-check-double" : "bx-circle"
              } me-1`}
            />
            {formik.values.is_default ? "Default Account" : "Mark as Default"}
          </Button>

          <Button
            type="button"
            color={formik.values.is_active ? "success" : "secondary"}
            outline={!formik.values.is_active}
            size="sm"
            onClick={() =>
              formik.setFieldValue("is_active", !formik.values.is_active)
            }
          >
            <i
              className={`bx ${
                formik.values.is_active ? "bx-check-circle" : "bx-x-circle"
              } me-1`}
            />
            {formik.values.is_active ? "Active" : "Inactive"}
          </Button>
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountBankAccountAddEdit
